import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { EgressCheck } from "@/api/admin-api"

import { AlertCircle, CheckCircle, Globe, RefreshCw, XCircle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useEgressDiagnosticsQuery } from "@/features/admin-dashboard/hooks/admin-queries"
import { cn } from "@/lib/utils"

// Egress proxy diagnostics card (#2645, companion to #2637). Answers "is the
// egress proxy healthy?" independently of the integrations that route through
// it: TCP reach, a probe through the proxy, and the same probe direct.

function CheckIcon({ status }: { status: EgressCheck["status"] }) {
  if (status === "pass") return <CheckCircle className="text-success size-4" />
  if (status === "fail") return <XCircle className="text-destructive size-4" />
  return <div className="border-muted size-4 rounded-full border-2" />
}

function CheckRow({ label, check }: { label: string; check: EgressCheck }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between border-b py-2 last:border-b-0">
      <div className="flex items-center gap-2">
        <CheckIcon status={check.status} />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-muted-foreground flex items-center gap-3 text-sm">
        {check.latency_ms !== undefined && <span>{check.latency_ms.toFixed(0)} ms</span>}
        {check.detail && <span title={check.detail}>{check.detail}</span>}
        <span
          className={cn(
            check.status === "pass" && "text-success",
            check.status === "fail" && "text-destructive"
          )}
        >
          {t(`adminDashboard.egressDiagnostics.status.${check.status}`, {
            defaultValue: check.status,
          })}
        </span>
      </div>
    </div>
  )
}

export function EgressDiagnosticsCard() {
  const { t } = useTranslation()
  const [requested, setRequested] = useState(false)
  const { data, isFetching, refetch, isError } = useEgressDiagnosticsQuery(requested)

  const runChecks = () => {
    setRequested(true)
    void refetch()
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Globe className="size-5" />
            {t("adminDashboard.egressDiagnostics.title", { defaultValue: "Egress Proxy" })}
            {data && (
              <span className="text-muted-foreground text-sm font-normal">
                {data.egress.mode === "proxy"
                  ? (data.egress.proxy ?? "")
                  : t("adminDashboard.egressDiagnostics.directEgress", {
                      defaultValue: "direct egress (no proxy configured)",
                    })}
              </span>
            )}
          </CardTitle>
          <Button disabled={isFetching} size="sm" variant="outline" onClick={runChecks}>
            <RefreshCw className={cn("mr-2 size-4", isFetching && "animate-spin")} />
            {t("adminDashboard.egressDiagnostics.runChecks", { defaultValue: "Run checks" })}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!requested && (
          <p className="text-muted-foreground py-4 text-center text-sm">
            {t("adminDashboard.egressDiagnostics.idleHint", {
              defaultValue:
                "Runs three bounded checks: proxy reachability, a request through the proxy, and the same request direct.",
            })}
          </p>
        )}
        {requested && isError && (
          <div className="text-destructive flex items-center gap-2 py-4 text-sm">
            <AlertCircle className="size-4" />
            {t("adminDashboard.egressDiagnostics.loadError", {
              defaultValue: "Failed to run egress diagnostics.",
            })}
          </div>
        )}
        {data && (
          <div className="space-y-3">
            <div>
              <CheckRow
                check={data.checks.tcp_reach}
                label={t("adminDashboard.egressDiagnostics.tcpReach", {
                  defaultValue: "Proxy reachable (TCP)",
                })}
              />
              <CheckRow
                check={data.checks.proxied_probe}
                label={t("adminDashboard.egressDiagnostics.proxiedProbe", {
                  defaultValue: "Request through proxy",
                })}
              />
              <CheckRow
                check={data.checks.direct_probe}
                label={t("adminDashboard.egressDiagnostics.directProbe", {
                  defaultValue: "Request direct (no proxy)",
                })}
              />
            </div>
            <div className="text-muted-foreground space-y-1 text-xs">
              <div>
                {t("adminDashboard.egressDiagnostics.proxiedIntegrations", {
                  defaultValue: "Proxied integrations",
                })}
                :{" "}
                {data.egress.proxied_integrations.length > 0
                  ? data.egress.proxied_integrations.join(", ")
                  : "—"}
              </div>
              {data.egress.ambient_vars_present && (
                <div className="text-warning-foreground flex items-center gap-1">
                  <AlertCircle className="size-3" />
                  {t("adminDashboard.egressDiagnostics.ambientVarsWarning", {
                    defaultValue:
                      "Legacy HTTP_PROXY/HTTPS_PROXY/NO_PROXY app settings are still present on this stamp — they are ignored by the backend and should be removed.",
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
